import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchPropertyList } from "../queries/property-list";
import { Property } from "./types";

import { MapContainer, TileLayer, Marker, ZoomControl, GeoJSON, LayerGroup } from 'react-leaflet';
import L, { LatLngExpression, LatLngTuple, Map } from "leaflet";
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
// import selectedIcon from 'leaflet/dist/images/marker-icon-2x.png';
import Offcanvas, { OffcanvasPlacement } from "react-bootstrap/esm/Offcanvas";
// import { Sheet, SheetRef } from 'react-modal-sheet';
import { BottomSheet } from 'react-spring-bottom-sheet';
import 'react-spring-bottom-sheet/dist/style.css'
import Card from "react-bootstrap/esm/Card";
import Button from "react-bootstrap/esm/Button";
import Image from "react-bootstrap/esm/Image";
import {BsChevronRight, BsCarFrontFill, BsSearch} from "react-icons/bs";
import {isMobile} from "react-device-detect";
import Row from "react-bootstrap/esm/Row";
import Col from "react-bootstrap/esm/Col";
import { Form, InputGroup, Spinner } from "react-bootstrap";
import { useDebounce } from "use-debounce";
import FuzzySearch from "fuzzy-search";
import { RefHandles, SpringEvent } from "react-spring-bottom-sheet/dist/types";

import shapesRaw from "./shapes.json";
import { GeoJsonObject } from "geojson";

type CountyProperty = {
    FeatId1?: Number;
    TAXIDNUM?: String;
    TAXSHORT?: Number;
    CALCACRES?: Number;
    MAP_ACRES?: Number;
    OWN1?: String;
    OWN2?: String;
    PHYSICAL_1?: String;
    PHYSICAL_2?: String;
    PHYSICAL_3?: String;
    MAILING_1?: String;
    MAILING_2?: String;
    CITYNAME?: String;
    STATE?: String;
    ZIP1?: String;
    ZIP2?: String;
    BOOK?: String;
    PAGE?: String;
    FRONTAGE?: Number;
    LEGAL1?: String;
    LEGAL2?: String;
    LAND?: Number;
    BLDG?: Number;
    TOTAL?: Number;
    SALEDATE?: String;
    PRICE?: String;
    YEAR_BUILT?: String;
    FOOTAGE?: String;
    CARD?: String;
    STORIES?: String;
    EXTWALL?: String;
    STYLE?: String;
    RMBED?: String;
    RMTOT?: String;
    FIXBATH?: String;
    FIXHALF?: String;
    FIXTOT?: String;
    RECROMAREA?: String;
    FINBSMTARE?: String;
    NBHD?: String;
    CNG?: String;
    LUC?: String;
    CLASS?: Number;
    SCHDIST?: Number;
    BEARING1?: String;
    BEARING2?: String;
    BEARING3?: String;
    BEARING4?: String;
    BEARING5?: String;
    BEARING6?: String;
    BEARING7?: String;
    BEARING8?: String;
    ZONE?: String;
    ZHBCASE?: String;
    SPECEXC?: String;
    DATE?: String;
    OLDLUC?: String;
    GROUP_1?: String;
    GROUP_2?: String;
    MARKETGROU?: String;
    TAXID?: String;
    PARID?: String;
    REFERENCE?: Number;
    ERROR?: String;
    CAMA_ACRES?: Number;
    GIS_ACRES?: Number;
    ACRE_ERROR?: Number;
    ERROR_PCT?: Number;
    COMMENTS?: String;
    COLOR?: String;
    SW_MEMBER?: Number;
    UNIQUE?: String;
};

type Shape = {
    type: String;
    properties: CountyProperty;
    geometry: {type: string, coordinates: []};
    pointLocation: {lat: number, lng: number};
}
const shapes: Record<string, Shape> = shapesRaw as unknown as Record<string, Shape>;
// const shapes: GeoJsonObject = shapesRaw as unknown as GeoJsonObject;

const IMAGE_BASE_URL = "https://assets.uniontownredevelopment.com/site-assets/properties/";
const DEFAULT_CENTER_COORDS: LatLngTuple = [39.901158, -79.725531];

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [35, 46],
    iconAnchor: [17, 46],
});

let SelectedIcon = L.icon({
    className: "selectedMapMarker",
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [35, 46],
    iconAnchor: [17, 46],
});

//L.tileLayer(`http://map1.vis.earthdata.nasa.gov/wmts-webmerc/MODIS_Terra_CorrectedReflectance_Bands367/default/{time}/{tilematrixset}{maxZoom}/{z}/{y}/{x}.{format}`);
// const OSM = L.tileLayer('http://{s}.tile.osm.org/{z}/{x}/{y}.png', {
//             maxZoom: 18,
//             attribution: 'Map data &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors, <a  href="http://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>'
//         });
        
L.Marker.prototype.options.icon = DefaultIcon;

let searcher: FuzzySearch<Property>;

const PropertyListScreen = (): ReactElement => {
    const {isPending, isError, isSuccess, data} = useQuery({queryKey: ["property-list"], queryFn: fetchPropertyList})
    const [showDetailList, setShowDetailList] = useState(true);
    const [detailListPlacement] = useState<OffcanvasPlacement>("start");
    const [selectedProperty, setSelectedProperty] = useState<Property | null>();
    const [viewData, setViewData] = useState<Property[] | undefined>();
    const [searching, setSearching] = useState<Boolean>(false);
    const [searchInputValue, setSearchInputValue] = useState<string | null>();
    const [searchValue] = useDebounce(searchInputValue, 400);
    const [mapHeight] = useState<number>(window.innerHeight);
    
    const bottomSheetRef = useRef<RefHandles>(null);
    const dataRef = useRef<Property[]>([]);
    const mapRef = useRef<Map | null>();
    const selectedItemDivRef = useRef<HTMLElement | null>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
   
    const scrollTo = (ref: HTMLDivElement): void => {
        ref?.scrollIntoView({behavior: "smooth", block: "start"});
        if(ref) {
            selectedItemDivRef.current = ref;
        }
    }

    // const setMapRef = (ref: L.Map): void => {
    //     mapRef.current = ref;
    //     if(!mapRef.current) {
    //         return;
    //     }

    //     L.geoJSON(Object.values(shapes)).addTo(mapRef.current);        
    // };

    // const setMapRef = (ref: L.Map): void => {
    //     mapRef.current = ref;
    //     if(!mapRef.current) {
    //         return;
    //     }
        
    //     OSM.addTo(mapRef.current);

    //     // L.gridLayer.googleMutant({
    //     //     type: "hybrid", // valid values are 'roadmap', 'satellite', 'terrain' and 'hybrid'
    //     // }).addTo(mapRef.current);
    // }

    const calcCoords = useCallback((prop: Property): LatLngExpression => {
        if(shapes[prop.parcelId]?.pointLocation) {
            // const shape = L.polygon(shapes[prop.parcelId].geometry.coordinates);
            // const c = shape.getBounds().getCenter();

            // console.log(`new: ${c.lat}, ${c.lng}`);

            // console.log(`pointLocation: ${JSON.stringify(shapes[prop.parcelId].pointLocation, null, 2)}`)
            // return [c.lng, c.lat];
            return [shapes[prop.parcelId].pointLocation.lng, shapes[prop.parcelId].pointLocation.lat];
        } else {
            const [x, y] = prop.rawCoords.split(",");
            return [parseFloat(x), parseFloat(y)]
        }
        
    }, []);

    const updateList = useCallback((): void => {
        if(!searchValue || !searcher) {
            setViewData(dataRef.current);
            return;
        }
        setSearching(true);
        setViewData(searcher.search(searchValue));
        setSearching(false);
    }, [searchValue]);

    useEffect(updateList, [searchValue, updateList]);
        
    //only applicable to mobile/bottomsheet approach to display property list
    const handleSpringEvent = (event: SpringEvent) => {
        if (event.type === "CLOSE") {
            //if use closed bottomsheet, then clear search input
            if(searchInputRef?.current) {
                searchInputRef.current.value = "";
                setSearchInputValue(null);
            }
        }
    }

    const handleHideOffcanvas = () => {
        setShowDetailList(false);
        if(searchInputRef?.current) {
            searchInputRef.current.value = "";
            setSearchInputValue(null);
        }
    }

    const handleMarkerSelected = (p: Property): void => {
        setShowDetailList(true);
        setSelectedProperty(p);
    };

    const to200x200 = (path: string): string => {
        if(!path) return "";

        const parts = path.split("/");
        return `IMAGES/400x/${parts[parts.length - 1]}`;
    }

    useEffect(() => {
        if(selectedProperty) {
            const [lat, lng] = selectedProperty.rawCoords.split(",");
            if(isMobile) {
                // console.log(`${mapRef.current?.getBounds().getNorth()} | ${mapRef.current?.getBounds().getSouth()} | ${lat} | ${lng}`)
                
                const northernLat = mapRef?.current?.getBounds().getNorth() || 0;
                const southernLat = mapRef?.current?.getBounds().getSouth() || 0;
                const degreesInMapViewPort = southernLat - northernLat;
                const quarterLatitudeDistance = degreesInMapViewPort/4;

                mapRef?.current?.setView([parseFloat(lat) + quarterLatitudeDistance, parseFloat(lng)], mapRef.current?.getZoom());
            } else {
                mapRef?.current?.setView([parseFloat(lat), parseFloat(lng)], mapRef.current?.getZoom());
            }

        }
    }, [selectedProperty]);

    useEffect(() => {
        if(isSuccess) {
            dataRef.current = data;
            searcher = new FuzzySearch(dataRef.current, ["parcelId", "address"], {caseSensitive: false})
            // setViewData(dataRef.current);
            updateList();
        }
    }, [isSuccess, data, updateList])

    if(isPending || (isSuccess && !viewData)) {
        return (
            <div>
                Loading...
            </div>
        );
    } else if(isSuccess && viewData) {
        return (
            <div>
                {!showDetailList && (
                    <Button 
                        style={{
                            position: "absolute",
                            zIndex: 10000,
                            ...(isMobile ? 
                                {
                                    bottom: "25px", 
                                    marginLeft: "auto", 
                                    marginRight: "auto",
                                    left: "0px",
                                    right: "0px", 
                                    width: "160px"
                                } : {
                                    top: "25px", 
                                    left: "0px"
                                })
                        }}
                        onClick={() => setShowDetailList(true)}
                    >
                        Show property list {!isMobile && <BsChevronRight/>}
                    </Button>
                )}
                {!isMobile && (
                    <Offcanvas 
                        show={showDetailList} 
                        backdrop={false}
                        placement={detailListPlacement} 
                        onHide={handleHideOffcanvas}
                        >
                        <Offcanvas.Header closeButton>   
                            <InputGroup style={{padding: "5px"}}>
                                    <Form.Control
                                        ref={searchInputRef}
                                        placeholder="Search..."
                                        aria-label="Search properties"
                                        aria-describedby="basic-addon2"
                                        onChange={(e) => { setSearchInputValue(e.target.value) }}
                                    /> 
                                    <InputGroup.Text id="basic-addon1">
                                        {searching && (
                                            <Spinner animation="border" variant="default" size="sm"/>
                                        )}
                                        {!searching && (
                                            <BsSearch/>
                                        )}
                                    </InputGroup.Text>  
                                </InputGroup>
                        </Offcanvas.Header>
                        <Offcanvas.Body style={{overflow: "hidden"}}>
                            <div 
                                style={{
                                overflowY: "scroll",
                                height: "100%",
                                width: "100%",
                                paddingBottom: "200px"
                            }}>
                                {viewData.map((item: Property, index) => (
                                    <Card 
                                        key={`property-card-${index}`}
                                        ref={selectedProperty?.parcelId === item.parcelId ? scrollTo : undefined}
                                        className={selectedProperty?.parcelId === item.parcelId ? "border-info" : ""}
                                        style={{
                                            marginBottom: "5px",
                                            ...(selectedProperty?.parcelId === item.parcelId ? {borderColor: "red", borderWidth: "3px"} : {})
                                        }}>
                                        {item.photoPath && (<Card.Img onError={(event) => event.currentTarget.style.display = "none"} style={{minHeight: "100%", minWidth: "100%"}} variant="top" src={`${IMAGE_BASE_URL}${to200x200(item.photoPath)}`} />) }
                                        <Card.Body>
                                            <Card.Title>{item.address.split(",")[0]}</Card.Title>
                                            <Card.Text>
                                                {item.address}
                                                {item.demolished === "YES" && (<><br/><span className="text-success">Structure demolished</span></>)}
                                                {item.status && (<><br/><span className={item.status === "FOR SALE" ? "text-success" : "text-danger"}>{item.status}</span></>)}
                                            </Card.Text>
                                            <Button variant="primary" onClick={() => setSelectedProperty(item)}>Show on map</Button> 
                                            <a target="_blank" style={{marginLeft: "5px"}} aria-label="Travelling directions" href={`https://www.google.com/maps/dir/current_location/${encodeURIComponent(item.address)}`} rel="noreferrer"><BsCarFrontFill/></a>  
                                            <br/><a target="_blank" href={item.countySitePropertyCard} rel="noreferrer">Parcel info</a>  
                                        </Card.Body>
                                    </Card>        
                                ))}
                            </div>
                        </Offcanvas.Body>
                    </Offcanvas>
                )}

                {isMobile && (
                    <BottomSheet 
                        ref={bottomSheetRef}
                        open={showDetailList} 
                        snapPoints={ ({headerHeight, footerHeight, height, minHeight, maxHeight}) => {
                            return [
                                maxHeight/2,
                                maxHeight
                            ];
                        } }
                        initialFocusRef={selectedItemDivRef}
                        defaultSnap={0}
                        style={{zIndex: 1000, position: "absolute"}}
                        onSpringEnd={handleSpringEvent}
                        onDismiss={ () => setShowDetailList(false) }
                        blocking={false}
                        header={(
                            <InputGroup style={{padding: "5px"}}>
                                <Form.Control
                                    ref={searchInputRef}                                    
                                    placeholder="Search..."
                                    aria-label="Search properties"
                                    aria-describedby="basic-addon2"
                                    onChange={(e) => { setSearchInputValue(e.target.value) }}
                                />
                            
                                <InputGroup.Text id="basic-addon1">
                                    {searching && (
                                        <Spinner animation="border" variant="default" size="sm"/>
                                    )}
                                    {!searching && (
                                        <BsSearch/>
                                    )}
                                </InputGroup.Text>
                            
                            </InputGroup>
                        )}
                    > 
                        {viewData.map((item: Property, index: number) => (
                            <Row 
                                key={`property-row-${index}`}
                                id={selectedProperty?.parcelId}
                                ref={selectedProperty?.parcelId === item.parcelId ? scrollTo : undefined}
                                className={selectedProperty?.parcelId === item.parcelId ? "bg-light border-info" : ""}
                                style={{
                                    borderTop: "2px solid #eee",
                                    marginBottom: "3px",
                                }}>
                                <Col xs={4} md={2}>
                                    {item.photoPath && (
                                        <Image 
                                            style={{border: "none", minHeight: "100%", minWidth: "100%", }} 
                                            alt="" 
                                            onError={(event) => event.currentTarget.style.display = "none"}
                                            src={`${IMAGE_BASE_URL}${to200x200(item.photoPath)}`} 
                                            thumbnail />
                                    )}
                                </Col>
                                <Col xs={6} md={8}>
                                    <b>{item.address.split(",")[0]}</b><br/>
                                        {item.demolished === "YES" && (<><span className="text-success">Demolished</span><br/></>)}
                                        {item.status && (<><span className={item.status === "FOR SALE" ? "text-success" : "text-danger"}>{item.status}</span><br/></>)}
                                        <Button variant="primary" onClick={() => setSelectedProperty(item)}>Show on map</Button>
                                        <a target="_blank" style={{marginLeft: "5px"}} aria-label="Travelling directions" href={`https://www.google.com/maps/dir/current_location/${encodeURIComponent(item.address)}`} rel="noreferrer"><BsCarFrontFill/></a>   
                                        <br/><a target="_blank" href={item.countySitePropertyCard} rel="noreferrer">Parcel info</a>           
                                </Col>        
                            </Row>       
                        ))}
                        
                    </BottomSheet>
                )}

                <MapContainer 
                    style={{height: `${mapHeight}px`}} 
                    ref={m => mapRef.current = m}
                    center={DEFAULT_CENTER_COORDS} 
                    zoom={15} 
                    zoomControl={false}
                    scrollWheelZoom={true}>
                    <ZoomControl position="topright"/>

                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />

                    <LayerGroup>
                        {viewData.filter(item => shapes[item.parcelId]).map((item: Property, index: number) => {
                                // return (<Polygon color="blue" positions={shapes[item.parcelId]} />); 
                                //console.log(JSON.stringify(shapes[item.parcelId], null, 2));
                                return <GeoJSON 
                                    key={Math.random()}
                                    style={{
                                        opacity: 1,
                                        fillOpacity: 0.4,
                                        weight: 2,
                                        color: "blue",
                                        fillColor: selectedProperty?.parcelId === item.parcelId ? "red" : "blue" ,
                                        fill: true,
                                    }} 
                                    eventHandlers={
                                        {
                                            load: (): void => {
                                                const latLng = L.polygon((item as unknown as Shape).geometry.coordinates.map(([lat, lng]: number[]):LatLngTuple => [lat, lng] )).getBounds().getCenter();
                                                shapes[item.parcelId].pointLocation = latLng;
                                            },
                                            click: () => {
                                                handleMarkerSelected(item);
                                            }
                                        }
                                    }
                                    // coordsToLatLng={(coords) => {
                                    //     const newCoords = L.GeoJSON.coordsToLatLng(coords);
                                    //     console.log(JSON.stringify(newCoords, null, 2));
                                    //     return newCoords;
                                    // }}
                                    data={shapes[item.parcelId].geometry as unknown as GeoJsonObject}>
                                    {/* <Tooltip>
                                        {item.address}
                                    </Tooltip> */}
                                </GeoJSON>
                            })}
                    </LayerGroup>

                    {/* <LayersControl position="topright">
                        <LayersControl.Overlay key="RACU_Properties" name="RACU Properties" checked>
                            {viewData.filter(item => shapes[item.parcelId]).map((item: Property) => {
                                // return (<Polygon color="blue" positions={shapes[item.parcelId].geometry.coordinates} />); 
                                return <GeoJSON 
                                    key={Math.random()}
                                    style={{
                                        opacity: 1,
                                        fillOpacity: 0.7,
                                        weight: 2,
                                        dashArray: "2",
                                        color: "blue"
                                    }} 
                                    data={shapes[item.parcelId].geometry}/>
                            })}
                        </LayersControl.Overlay>
                    </LayersControl> */}

                    {selectedProperty?.rawCoords && (
                        <Marker 
                            key={`property-marker`} 
                            position={calcCoords(selectedProperty)}
                            // riseOnHover={true}
                            icon={SelectedIcon}
                            >
                        </Marker>
                    )}

                    {/* {viewData.map((item: Property, index: number) => {
                        if(!shapes[item.parcelId]) {
                            return null;
                        }
                        const [lat, lng] = item.rawCoords.split(",");
                        //const {lat, lng} = shapes[item.parcelId].pointLocation;
                        return (
                            <Marker 
                                key={`property-marker-${index}`} 
                                position={[Number(lat), Number(lng)]}
                                riseOnHover={true}
                                icon={selectedProperty?.parcelId === item.parcelId ? SelectedIcon : DefaultIcon}
                                eventHandlers={{
                                    click: () => handleMarkerSelected(item)
                                }}>
                                <Tooltip>
                                    {item.address}
                                </Tooltip>
                            </Marker>
                        )}
                    )} */}
                    
                </MapContainer>
            </div>
        )
    } else if(isError) {
        return (
            <div>
                Something went wrong.
            </div>
        )
    }

    return (<div/>);
}

export default PropertyListScreen;